import React, {useState, useEffect, useMemo} from "react";
import {
    Box, Container, Paper, Stack, Button, FormControl, InputLabel, Select, MenuItem,
    useMediaQuery, Chip, Tooltip, Card, CardContent, Typography, Grid, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useTheme} from "@mui/material/styles";
import MainLayout from "../MainLoyout/MainLayout";
import Api from "../../Services/Api";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import Skeleton from "@mui/material/Skeleton";
import {DesktopDatePicker, MobileDatePicker} from "@mui/x-date-pickers";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Title as ChartTitle,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js";
import {Doughnut, Bar} from "react-chartjs-2";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, ChartTitle, ChartTooltip, Legend);

// One color per conv type. Order = display order (biggest types first).
const CONV_FIELDS = [
    {field: "conv_yhbgindirect",   label: "YH BG indir",  color: "#ef4444"},
    {field: "conv_formfilling",    label: "Form Fill",    color: "#f59e0b"},
    {field: "conv_adsense",        label: "AdSense",      color: "#10b981"},
    {field: "conv_genericrelated", label: "Generic Rel",  color: "#3b82f6"},
    {field: "conv_tb",             label: "Taboola",      color: "#8b5cf6"},
    {field: "conv_yhbgdbl",        label: "YH BG dbl",    color: "#ec4899"},
    {field: "conv_ob",             label: "Outbrain",     color: "#06b6d4"},
    {field: "conv_affiliation",    label: "Affiliation",  color: "#84cc16"},
    {field: "conv_afd2clicks",     label: "AFD 2-clk",    color: "#f97316"},
    {field: "conv_afs",            label: "Yahoo AFS",    color: "#64748b"},
];

const fmt = (v) => {
    if (v === null || v === undefined || isNaN(v)) return "0";
    return Number(v).toLocaleString();
};

const fmtPct = (v) => (v === null || v === undefined || isNaN(v)) ? "—" : `${Number(v).toFixed(1)}%`;

const colorForPct = (pct) => {
    // Heatmap intensity from low (gray) → high (vivid). Returns an rgba string.
    if (!pct || pct < 0.1) return "transparent";
    const alpha = Math.min(1, pct / 60); // saturated around 60%
    return `rgba(59, 130, 246, ${alpha * 0.18})`; // subtle blue tint
};

const KPI = ({label, value, sub, color = "primary.main"}) => (
    <Card variant="outlined" sx={{height: "100%"}}>
        <CardContent sx={{p: 2, "&:last-child": {pb: 2}}}>
            <Typography variant="caption" color="text.secondary" sx={{textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600}}>
                {label}
            </Typography>
            <Typography variant="h4" sx={{fontWeight: 700, color, mt: 0.5, lineHeight: 1.1}}>
                {value}
            </Typography>
            {sub && (
                <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: "block"}}>
                    {sub}
                </Typography>
            )}
        </CardContent>
    </Card>
);

const SkeletonRows = () => (
    <Box sx={{height: "max-content"}}>
        {[...Array(12)].map((_, k) => (
            <Skeleton key={k} variant="rectangular" sx={{my: 2, mx: 1}}/>
        ))}
    </Box>
);

const Conversions = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [startDate, setStartDate] = useState(dayjs().subtract(1, "day"));
    const [endDate, setEndDate] = useState(dayjs().subtract(1, "day"));
    const [groupBy, setGroupBy] = useState("internal_name");
    const [hourlyData, setHourlyData] = useState(null);

    // Drill-down dialog for "Advertiser Landings" (bunnycdn_advertiser_landings).
    const [landingsOpen, setLandingsOpen] = useState(false);
    const [landingsRow, setLandingsRow] = useState(null);
    const [landingsData, setLandingsData] = useState([]);
    const [landingsLoading, setLandingsLoading] = useState(false);
    const [landingsGroupBy, setLandingsGroupBy] = useState("host"); // host | url
    const [landingsConvFilter, setLandingsConvFilter] = useState("all"); // all | conv_only | landings_only
    const [landingsSummary, setLandingsSummary] = useState(null);

    const fetchLandings = async (tag, gb, cf) => {
        setLandingsLoading(true);
        setLandingsData([]);
        const res = await Api.tigerAdvertiserLandings({
            internal_name: tag,
            from: startDate.format("YYYY-MM-DD"),
            to: endDate.format("YYYY-MM-DD"),
            limit: 100,
            group_by: gb,
            conv_filter: cf,
        });
        if (res && res.success) {
            setLandingsData(res.data || []);
            setLandingsSummary(res.summary || null);
        }
        setLandingsLoading(false);
    };

    const openLandings = async (row) => {
        const tag = row.internal_name || row.name;
        if (!tag) return;
        setLandingsRow(row);
        setLandingsOpen(true);
        await fetchLandings(tag, landingsGroupBy, landingsConvFilter);
    };

    const handleLandingsGroupBy = async (gb) => {
        setLandingsGroupBy(gb);
        const tag = landingsRow?.internal_name || landingsRow?.name;
        if (tag) await fetchLandings(tag, gb, landingsConvFilter);
    };

    const handleLandingsConvFilter = async (cf) => {
        setLandingsConvFilter(cf);
        const tag = landingsRow?.internal_name || landingsRow?.name;
        if (tag) await fetchLandings(tag, landingsGroupBy, cf);
    };

    const fetchData = async (from, to, gb) => {
        setLoading(true);
        const fromStr = from.format("YYYY-MM-DD");
        const toStr = to.format("YYYY-MM-DD");

        // Main daily breakdown
        const result = await Api.tigerStats({from: fromStr, to: toStr, group_by: gb});
        if (result && result.success) {
            const rows = (result.data || []).map((r, idx) => {
                const total_conv = CONV_FIELDS.reduce((s, c) => s + (Number(r[c.field]) || 0), 0);
                const vc = Number(r.valid_clicks) || 0;
                const cr_pct = vc > 0 ? Number((total_conv / vc * 100).toFixed(2)) : 0;
                const pctRow = {};
                CONV_FIELDS.forEach(({field}) => {
                    pctRow[`${field}_pct`] = total_conv > 0 ? (Number(r[field] || 0) / total_conv * 100) : 0;
                });
                return {...r, id: idx, total_conv, cr_pct, ...pctRow};
            });
            setData(rows);
            setSummary(result.summary || null);
        } else {
            setData([]);
            setSummary(null);
        }

        // Hourly breakdown for the same range (independent — failure here
        // shouldn't block the rest of the page).
        try {
            const hourlyResult = await Api.tigerHourlyStats({from: fromStr, to: toStr, group_by: "hour"});
            if (hourlyResult && hourlyResult.success) {
                setHourlyData(hourlyResult.data || []);
            } else {
                setHourlyData([]);
            }
        } catch (e) {
            setHourlyData([]);
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchData(startDate, endDate, groupBy);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePreset = (days) => {
        const to = dayjs().subtract(1, "day");
        const from = dayjs().subtract(days, "day");
        setStartDate(from);
        setEndDate(to);
        fetchData(from, to, groupBy);
    };

    const handleDateChange = (type, value) => {
        if (type === "start") {
            setStartDate(value);
            fetchData(value, endDate, groupBy);
        } else {
            setEndDate(value);
            fetchData(startDate, value, groupBy);
        }
    };

    const handleGroupByChange = (value) => {
        setGroupBy(value);
        fetchData(startDate, endDate, value);
    };

    const showFeedCols = groupBy === "internal_name" || groupBy === "name";

    // Aggregated metrics
    const aggregates = useMemo(() => {
        if (!summary) return null;
        const total_conv = CONV_FIELDS.reduce((s, c) => s + (Number(summary[c.field]) || 0), 0);
        const vc = Number(summary.valid_clicks) || 0;
        const cr_pct = vc > 0 ? (total_conv / vc * 100) : 0;
        const feedsWithConv = data.filter(r => r.total_conv > 0).length;
        return {total_conv, valid_clicks: vc, cr_pct, feedsWithConv, distinctRows: data.length};
    }, [summary, data]);

    // Doughnut chart: overall mix
    const doughnutData = useMemo(() => {
        if (!summary) return null;
        const labels = [];
        const values = [];
        const colors = [];
        CONV_FIELDS.forEach(({field, label, color}) => {
            const v = Number(summary[field] || 0);
            if (v > 0) {
                labels.push(label);
                values.push(v);
                colors.push(color);
            }
        });
        return {
            labels,
            datasets: [{data: values, backgroundColor: colors, borderWidth: 1, borderColor: "#fff"}],
        };
    }, [summary]);

    // Hourly stacked bar (0..23 UTC) — answers "when do convs happen?"
    const hourlyChartData = useMemo(() => {
        if (!hourlyData || !hourlyData.length) return null;
        // Build a 24-slot array filled with the hourlyData rows by hour.
        const byHour = {};
        hourlyData.forEach(r => { byHour[r.hour] = r; });
        const labels = [];
        for (let h = 0; h < 24; h++) labels.push(h.toString().padStart(2, "0") + ":00");
        const datasets = CONV_FIELDS.map(({field, label, color}) => ({
            label,
            data: Array.from({length: 24}, (_, h) => Number(byHour[h]?.[field] || 0)),
            backgroundColor: color,
            borderWidth: 0,
        }));
        return {labels, datasets};
    }, [hourlyData]);

    // Top-feeds bar chart (top 10 by total_conv)
    const topFeedsData = useMemo(() => {
        if (!data.length) return null;
        const sorted = [...data].sort((a, b) => b.total_conv - a.total_conv).slice(0, 10);
        const labels = sorted.map(r => {
            const name = r.feed?.supply_feed_name || r.name || r.internal_name || r.date || r.hostname || "?";
            return String(name).slice(0, 22);
        });
        // Stacked bar per conv type
        const datasets = CONV_FIELDS.map(({field, label, color}) => ({
            label,
            data: sorted.map(r => Number(r[field] || 0)),
            backgroundColor: color,
            borderWidth: 0,
        }));
        return {labels, datasets};
    }, [data]);

    const groupColumn = useMemo(() => {
        if (groupBy === "date") return {field: "date", headerName: "Date", width: 130};
        if (groupBy === "name") return {field: "name", headerName: "Name", flex: 1, minWidth: 150};
        if (groupBy === "hostname") return {field: "hostname", headerName: "Host", flex: 1, minWidth: 150};
        return {field: "internal_name", headerName: "Internal", width: 110};
    }, [groupBy]);

    // Pre-compute column sums for column-share alternative coloring (unused for now,
    // could expose as toggle later).

    const columns = useMemo(() => [
        groupColumn,
        ...(groupBy === "name" ? [{field: "internal_name", headerName: "Internal", width: 100}] : []),
        ...(showFeedCols ? [
            {
                field: "supply_name",
                headerName: "Supply feed",
                flex: 1,
                minWidth: 170,
                valueGetter: (p) => p.row.feed?.supply_feed_name || "-",
                renderCell: (p) => {
                    const f = p.row.feed;
                    if (!f) return <span style={{color: "#999"}}>-</span>;
                    return (
                        <Tooltip
                            title={`sf=${f.supply_feed_id} · ${f.publisher || "?"} · ${f.active ? "active" : "inactive"}`}
                            arrow
                        >
                            <span style={{
                                fontWeight: 500,
                                color: f.active ? undefined : "#999",
                            }}>{f.supply_feed_name}</span>
                        </Tooltip>
                    );
                },
            },
            {
                field: "demands",
                headerName: "Demands",
                flex: 1.4,
                minWidth: 220,
                sortable: false,
                valueGetter: (p) => (p.row.feed?.demand_feeds || []).map(d => d.advertiser).join(", "),
                renderCell: (p) => {
                    const ds = p.row.feed?.demand_feeds || [];
                    if (!ds.length) return <span style={{color: "#999"}}>-</span>;
                    return (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{gap: 0.5, py: 0.5}}>
                            {ds.map((d) => (
                                <Tooltip
                                    key={d.demand_feed_id}
                                    title={`df=${d.demand_feed_id} · needle="${d.needle}" · ${d.status === 1 ? "active" : "paused"}`}
                                    arrow
                                >
                                    <Chip
                                        label={d.advertiser || d.needle || `df${d.demand_feed_id}`}
                                        size="small"
                                        variant={d.status === 1 ? "filled" : "outlined"}
                                        color={d.status === 1 ? "primary" : "default"}
                                        sx={{height: 20, fontSize: "0.7rem"}}
                                    />
                                </Tooltip>
                            ))}
                        </Stack>
                    );
                },
            },
        ] : []),
        {
            field: "valid_clicks",
            headerName: "Valid clicks",
            type: "number",
            width: 105,
            renderCell: (p) => <span>{fmt(p.value)}</span>,
        },
        {
            field: "total_conv",
            headerName: "Conv",
            type: "number",
            width: 95,
            renderCell: (p) => <strong style={{fontVariantNumeric: "tabular-nums"}}>{fmt(p.value)}</strong>,
        },
        {
            field: "cr_pct",
            headerName: "CR%",
            type: "number",
            width: 85,
            renderCell: (p) => {
                const v = p.value;
                let color = "default";
                if (v >= 30) color = "success";
                else if (v >= 10) color = "info";
                else if (v >= 3) color = "warning";
                else if (v > 0) color = "error";
                return <Chip label={`${v}%`} size="small" color={color} variant="outlined"/>;
            },
        },
        // Advertiser landings (BunnyCDN) — clickable to drill into top URLs.
        {
            field: "bunnycdn_landings",
            headerName: "Adv. Landings",
            type: "number",
            width: 120,
            renderCell: (p) => {
                const v = Number(p.value || 0);
                if (v === 0) return <span style={{color: "#bbb", fontVariantNumeric: "tabular-nums"}}>0</span>;
                return (
                    <Tooltip title="Click for top advertiser URLs">
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => openLandings(p.row)}
                            sx={{
                                p: 0, minWidth: 0, fontWeight: 600,
                                fontVariantNumeric: "tabular-nums",
                                textTransform: "none",
                            }}
                        >
                            {fmt(v)}
                        </Button>
                    </Tooltip>
                );
            },
        },
        // Per-conv-type columns: count + per-row %
        ...CONV_FIELDS.map(({field, label, color}) => ({
            field,
            headerName: label,
            type: "number",
            width: 115,
            renderCell: (p) => {
                const count = Number(p.value || 0);
                const pct = Number(p.row[`${field}_pct`] || 0);
                if (count === 0) {
                    return <span style={{color: "#bbb", fontVariantNumeric: "tabular-nums"}}>0</span>;
                }
                return (
                    <Box sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "flex-end",
                        bgcolor: colorForPct(pct),
                        px: 1,
                        borderLeft: pct >= 30 ? `2px solid ${color}` : "none",
                    }}>
                        <Typography variant="body2" sx={{fontWeight: 500, lineHeight: 1.1, fontVariantNumeric: "tabular-nums"}}>
                            {fmt(count)}
                        </Typography>
                        <Typography variant="caption" sx={{color: "text.secondary", lineHeight: 1, fontSize: "0.65rem"}}>
                            {pct.toFixed(1)}%
                        </Typography>
                    </Box>
                );
            },
        })),
    ], [groupBy, groupColumn, showFeedCols]);

    const DatePickerComponent = isMobile ? MobileDatePicker : DesktopDatePicker;

    return (
        <MainLayout>
            <Container maxWidth="xl" sx={{mt: isMobile ? 2 : 3, mb: 4, px: isMobile ? 1 : 3}}>
                {/* Page header */}
                <Box sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "stretch" : "center",
                    gap: 1.5,
                    mb: 2.5,
                }}>
                    <Box>
                        <Typography variant="h4" sx={{fontWeight: 700, lineHeight: 1.2}}>
                            Conversions
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Per-source conversions tracked from tiger_stats — counts and mix per feed.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{gap: 0.5}}>
                        <Button size="small" variant="outlined" onClick={() => handlePreset(1)}>1d</Button>
                        <Button size="small" variant="outlined" onClick={() => handlePreset(7)}>7d</Button>
                        <Button size="small" variant="outlined" onClick={() => handlePreset(14)}>14d</Button>
                        <Button size="small" variant="outlined" onClick={() => handlePreset(30)}>30d</Button>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePickerComponent
                                label="From"
                                value={startDate}
                                onChange={(d) => handleDateChange("start", d)}
                                slotProps={{textField: {size: "small", sx: {width: isMobile ? 120 : 140}}}}
                            />
                            <DatePickerComponent
                                label="To"
                                value={endDate}
                                onChange={(d) => handleDateChange("end", d)}
                                slotProps={{textField: {size: "small", sx: {width: isMobile ? 120 : 140}}}}
                            />
                        </LocalizationProvider>
                        <FormControl size="small" sx={{minWidth: 150}}>
                            <InputLabel>Group by</InputLabel>
                            <Select
                                label="Group by"
                                value={groupBy}
                                onChange={(e) => handleGroupByChange(e.target.value)}
                            >
                                <MenuItem value="internal_name">Internal name</MenuItem>
                                <MenuItem value="name">Name</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="hostname">Hostname</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Box>

                {/* KPI cards */}
                {aggregates && (
                    <Grid container spacing={2} sx={{mb: 2.5}}>
                        <Grid item xs={6} md={3}>
                            <KPI
                                label="Total conversions"
                                value={fmt(aggregates.total_conv)}
                                sub={`across ${aggregates.feedsWithConv} feeds`}
                                color="primary.main"
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <KPI
                                label="Valid clicks"
                                value={fmt(aggregates.valid_clicks)}
                                sub="Tiger valid_clicks"
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <KPI
                                label="Avg CR"
                                value={fmtPct(aggregates.cr_pct)}
                                sub="conv / valid_clicks"
                                color={aggregates.cr_pct >= 20 ? "success.main" : aggregates.cr_pct >= 5 ? "info.main" : "warning.main"}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <KPI
                                label="Rows"
                                value={fmt(aggregates.distinctRows)}
                                sub={`grouped by ${groupBy}`}
                            />
                        </Grid>
                    </Grid>
                )}

                {/* Charts */}
                {!loading && doughnutData && (
                    <Grid container spacing={2} sx={{mb: 2.5}}>
                        <Grid item xs={12} md={5}>
                            <Paper variant="outlined" sx={{p: 2, height: "100%"}}>
                                <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
                                    Conversion mix
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{display: "block", mb: 1.5}}>
                                    Share of total convs per source
                                </Typography>
                                <Box sx={{height: 280, display: "flex", justifyContent: "center"}}>
                                    <Doughnut
                                        data={doughnutData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {position: "right", labels: {boxWidth: 12, font: {size: 11}}},
                                                tooltip: {
                                                    callbacks: {
                                                        label: (ctx) => {
                                                            const total = ctx.dataset.data.reduce((s, v) => s + v, 0);
                                                            const pct = total > 0 ? (ctx.parsed / total * 100).toFixed(1) : 0;
                                                            return `${ctx.label}: ${ctx.parsed.toLocaleString()} (${pct}%)`;
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Paper variant="outlined" sx={{p: 2, height: "100%"}}>
                                <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
                                    Top 10 feeds
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{display: "block", mb: 1.5}}>
                                    Stacked conversions by source
                                </Typography>
                                <Box sx={{height: 280}}>
                                    {topFeedsData && (
                                        <Bar
                                            data={topFeedsData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                indexAxis: "y",
                                                plugins: {
                                                    legend: {position: "bottom", labels: {boxWidth: 10, font: {size: 10}}},
                                                    tooltip: {
                                                        callbacks: {
                                                            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x.toLocaleString()}`,
                                                        },
                                                    },
                                                },
                                                scales: {
                                                    x: {stacked: true, ticks: {font: {size: 10}}},
                                                    y: {stacked: true, ticks: {font: {size: 10}}},
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {/* Hourly distribution */}
                {!loading && hourlyChartData && (
                    <Paper variant="outlined" sx={{p: 2, mb: 2.5}}>
                        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1}}>
                            <Box>
                                <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                    Conversions by hour of day (UTC)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Stacked by source. Empty hours = no data yet from tiger_hourly_stats (populates as the cron runs).
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{height: 280}}>
                            <Bar
                                data={hourlyChartData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: {position: "bottom", labels: {boxWidth: 10, font: {size: 10}}},
                                        tooltip: {
                                            mode: "index",
                                            callbacks: {
                                                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()}`,
                                                footer: (items) => {
                                                    const total = items.reduce((s, i) => s + (i.parsed.y || 0), 0);
                                                    return `Total: ${total.toLocaleString()}`;
                                                },
                                            },
                                        },
                                    },
                                    scales: {
                                        x: {stacked: true, ticks: {font: {size: 10}}},
                                        y: {stacked: true, ticks: {font: {size: 10}}},
                                    },
                                }}
                            />
                        </Box>
                    </Paper>
                )}

                {/* Per-source totals strip */}
                {summary && (
                    <Paper variant="outlined" sx={{p: 1.5, mb: 2.5}}>
                        <Typography variant="caption" color="text.secondary" sx={{textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, mb: 1, display: "block"}}>
                            Totals by source
                        </Typography>
                        <Grid container spacing={1}>
                            {CONV_FIELDS.map(({field, label, color}) => {
                                const v = Number(summary[field] || 0);
                                const pct = aggregates && aggregates.total_conv > 0 ? (v / aggregates.total_conv * 100) : 0;
                                return (
                                    <Grid item xs={6} sm={4} md={2.4} key={field}>
                                        <Box sx={{
                                            p: 1,
                                            borderLeft: `3px solid ${color}`,
                                            bgcolor: "action.hover",
                                            borderRadius: "0 4px 4px 0",
                                        }}>
                                            <Typography variant="caption" sx={{fontSize: "0.7rem", color: "text.secondary", fontWeight: 500}}>
                                                {label}
                                            </Typography>
                                            <Typography variant="body2" sx={{fontWeight: 700, fontVariantNumeric: "tabular-nums"}}>
                                                {fmt(v)}
                                                <Typography component="span" variant="caption" sx={{ml: 0.5, color: "text.secondary"}}>
                                                    {pct.toFixed(1)}%
                                                </Typography>
                                            </Typography>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Paper>
                )}

                {/* Detail table */}
                <Paper variant="outlined" sx={{p: isMobile ? 1 : 1.5}}>
                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, px: 1}}>
                        <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                            Detail per {groupBy.replace("_", " ")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Each conv cell shows count + % of that row's total
                        </Typography>
                    </Box>
                    {loading ? (
                        <SkeletonRows/>
                    ) : (
                        <Box sx={{height: 700, width: "100%"}}>
                            <DataGrid
                                rows={data}
                                columns={columns}
                                pageSize={50}
                                rowsPerPageOptions={[25, 50, 100, 200]}
                                disableSelectionOnClick
                                density="compact"
                                components={{Toolbar: GridToolbar}}
                                getRowHeight={() => showFeedCols ? 'auto' : 52}
                                initialState={{
                                    sorting: {sortModel: [{field: "total_conv", sort: "desc"}]},
                                }}
                                sx={{
                                    '& .MuiDataGrid-cell': {py: showFeedCols ? 0.5 : undefined, fontVariantNumeric: "tabular-nums"},
                                    '& .MuiDataGrid-columnHeaders': {bgcolor: "background.default", borderBottom: "2px solid"},
                                    '& .MuiDataGrid-columnHeaderTitle': {fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.3},
                                }}
                            />
                        </Box>
                    )}
                </Paper>
            </Container>

            {/* Drill-down: top advertiser URLs for the selected row */}
            <Dialog
                open={landingsOpen}
                onClose={() => setLandingsOpen(false)}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, flexWrap: "wrap"}}>
                    <Box sx={{minWidth: 240}}>
                        <Typography variant="h6" sx={{fontWeight: 700}}>
                            Advertiser landings & conversions
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {landingsRow?.internal_name || landingsRow?.name} ·{" "}
                            {startDate.format("YYYY-MM-DD")} → {endDate.format("YYYY-MM-DD")}
                        </Typography>
                        {landingsSummary && (
                            <Stack direction="row" spacing={1} sx={{mt: 1}} flexWrap="wrap">
                                <Chip size="small" label={`Clicks ${fmt(landingsSummary.clicks)}`}/>
                                <Chip size="small" color="primary" variant="outlined" label={`Landings ${fmt(landingsSummary.landings)}`}/>
                                <Chip size="small" color="success" label={`Conv ${fmt(landingsSummary.conversions)}`}/>
                                {landingsSummary.by_type?.slice(0, 4).map((t) => (
                                    <Chip key={t.conv_type} size="small" variant="outlined"
                                          label={`${t.conv_type}: ${fmt(t.conversions)}`}/>
                                ))}
                            </Stack>
                        )}
                    </Box>
                    <Stack direction="column" spacing={1} alignItems="flex-end">
                        <Stack direction="row" spacing={1}>
                            <Button size="small" variant={landingsGroupBy === "host" ? "contained" : "outlined"} onClick={() => handleLandingsGroupBy("host")}>By Host</Button>
                            <Button size="small" variant={landingsGroupBy === "url" ? "contained" : "outlined"} onClick={() => handleLandingsGroupBy("url")}>By URL</Button>
                            <IconButton onClick={() => setLandingsOpen(false)} size="small"><CloseIcon/></IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" variant={landingsConvFilter === "all" ? "contained" : "outlined"} onClick={() => handleLandingsConvFilter("all")}>All</Button>
                            <Button size="small" variant={landingsConvFilter === "conv_only" ? "contained" : "outlined"} color="success" onClick={() => handleLandingsConvFilter("conv_only")}>Conv only</Button>
                            <Button size="small" variant={landingsConvFilter === "landings_only" ? "contained" : "outlined"} onClick={() => handleLandingsConvFilter("landings_only")}>Landings only</Button>
                        </Stack>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {landingsLoading && <LinearProgress/>}
                    {!landingsLoading && landingsData.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{p: 2, textAlign: "center"}}>
                            No advertiser landings found for this period.
                        </Typography>
                    )}
                    {landingsData.length > 0 && (
                        <Box sx={{height: 500, width: "100%"}}>
                            <DataGrid
                                rows={landingsData.map((r, idx) => ({id: idx, ...r}))}
                                columns={landingsGroupBy === "host" ? [
                                    {
                                        field: "host",
                                        headerName: "Host",
                                        flex: 1,
                                        minWidth: 240,
                                        renderCell: (p) => (
                                            <Tooltip title={p.value || ""}>
                                                <Typography variant="body2" sx={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                                    {p.value}
                                                </Typography>
                                            </Tooltip>
                                        ),
                                    },
                                    {field: "clicks", headerName: "Total", type: "number", width: 90},
                                    {field: "landings", headerName: "Landings", type: "number", width: 100},
                                    {
                                        field: "conversions",
                                        headerName: "Conv",
                                        type: "number",
                                        width: 90,
                                        renderCell: (p) => {
                                            const v = Number(p.value || 0);
                                            return <Chip size="small" color={v > 0 ? "success" : "default"} variant={v > 0 ? "filled" : "outlined"} label={v}/>;
                                        },
                                    },
                                    {field: "unique_urls", headerName: "URLs", type: "number", width: 80},
                                    {field: "days_seen", headerName: "Days", type: "number", width: 70},
                                    {field: "last_seen", headerName: "Last seen", width: 110},
                                ] : [
                                    {
                                        field: "landing_url",
                                        headerName: "Landing URL",
                                        flex: 1,
                                        minWidth: 260,
                                        renderCell: (p) => (
                                            <Tooltip title={p.value}>
                                                <Typography variant="body2" sx={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                                    {p.value}
                                                </Typography>
                                            </Tooltip>
                                        ),
                                    },
                                    {field: "clicks", headerName: "Total", type: "number", width: 90},
                                    {field: "landings", headerName: "Landings", type: "number", width: 100},
                                    {
                                        field: "conversions",
                                        headerName: "Conv",
                                        type: "number",
                                        width: 90,
                                        renderCell: (p) => {
                                            const v = Number(p.value || 0);
                                            return <Chip size="small" color={v > 0 ? "success" : "default"} variant={v > 0 ? "filled" : "outlined"} label={v}/>;
                                        },
                                    },
                                    {field: "days_seen", headerName: "Days", type: "number", width: 70},
                                    {field: "last_seen", headerName: "Last seen", width: 110},
                                ]}
                                pageSize={50}
                                density="compact"
                                disableSelectionOnClick
                                initialState={{
                                    sorting: {sortModel: [{field: "clicks", sort: "desc"}]},
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLandingsOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </MainLayout>
    );
};

export default Conversions;
